import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * VoiceItemButton
 * ---------------
 * Mic button that records the user's voice, uses the browser's Web Speech API
 * for live pt-BR transcription, then sends the final transcript to the backend
 * AI endpoint `/api/ai/parse-item-voice` which returns { description, quantity, unit_value }.
 *
 * Props:
 *   onParsed({ description, quantity, unit_value }) — invoked after AI parse
 *   disabled — bool
 */
export default function VoiceItemButton({ onParsed, disabled }) {
  const [listening, setListening] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [partial, setPartial] = useState("");
  const recognitionRef = useRef(null);
  const finalRef = useRef("");

  const SR =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const supported = Boolean(SR);

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch {}
  };

  const toggle = () => {
    if (!supported) {
      toast.error("Seu navegador não suporta reconhecimento de voz. Use Chrome ou Edge no celular.");
      return;
    }
    if (listening) { stop(); return; }

    const rec = new SR();
    rec.lang = "pt-BR";
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    finalRef.current = "";
    setPartial("");

    rec.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (final) finalRef.current += final + " ";
      setPartial((finalRef.current + interim).trim());
    };

    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed") {
        toast.error("Permissão de microfone negada. Libere nas configurações do navegador.");
      } else if (e.error === "no-speech") {
        toast.info("Nenhuma fala detectada — tente de novo");
      } else {
        toast.error(`Erro de voz: ${e.error}`);
      }
    };

    rec.onend = async () => {
      setListening(false);
      const text = finalRef.current.trim();
      if (!text) return;
      setParsing(true);
      try {
        const r = await api.post("/ai/parse-item-voice", { text });
        onParsed?.(r.data);
        toast.success("Item preenchido pela voz");
      } catch (err) {
        // Fallback: still fill description with raw transcript
        onParsed?.({ description: text });
        toast.warning("Transcrevi, mas falhei em separar quantidade/valor");
      } finally {
        setParsing(false);
        setPartial("");
      }
    };

    try {
      rec.start();
      setListening(true);
      recognitionRef.current = rec;
    } catch (e) {
      toast.error("Não foi possível iniciar o microfone");
    }
  };

  useEffect(() => () => stop(), []);

  const busy = listening || parsing;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={toggle}
        disabled={disabled || parsing || !supported}
        data-testid="btn-voice-item"
        title={supported ? "Ditar item por voz (pt-BR)" : "Reconhecimento de voz não suportado"}
        className={`inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-full text-[11px] font-bold transition-all ${
          !supported
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : listening
            ? "bg-red-500 hover:bg-red-600 text-white animate-pulse shadow-lg shadow-red-200"
            : "border border-rose-200 text-rose-600 hover:bg-rose-50 bg-white"
        }`}
      >
        {parsing ? <Loader2 className="w-3 h-3 animate-spin" />
          : listening ? <MicOff className="w-3 h-3" />
          : <Mic className="w-3 h-3" />}
        {parsing ? "Entendendo..." : listening ? "Ouvindo..." : "Ditar por voz"}
      </button>
      {busy && partial && (
        <span className="text-[10px] text-slate-500 italic truncate max-w-[180px]" title={partial}>
          “{partial}”
        </span>
      )}
    </div>
  );
}
